import React, { useState, useRef } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Upload, Loader2, Image as ImageIcon, FileSpreadsheet, Plus, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';

const MANUAL_FIELDS = [
  ['headline', 'Deal-Überschrift', 'z. B. Arcus Infrastructure Partners buys TrueNoord'], ['target', 'Target', 'TrueNoord'],
  ['fund_vehicle', 'Fund-Vehikel', 'Arcus European Infrastructure Fund IV'], ['value', 'Wert', '$61 m'],
  ['date', 'Datum', '2026-04-29'], ['status', 'Status', 'Completed'], ['sector', 'Sektor', 'Transport'], ['country', 'Land', 'Netherlands'],
  ['region', 'Region', 'Western Europe'], ['description', 'Beschreibung', 'Regional aircraft lessor…'],
];

export function DealImportDialog({ fundId, fundName, open, onClose, onSaved }) {
  const [files, setFiles] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [rows, setRows] = useState(null);
  const [errors, setErrors] = useState([]);
  const [include, setInclude] = useState({});
  const [saving, setSaving] = useState(false);
  const [manual, setManual] = useState({});
  const inputRef = useRef(null);

  const reset = () => { setFiles([]); setRows(null); setErrors([]); setInclude({}); setManual({}); };
  const close = () => { reset(); onClose(); };

  const addFiles = (list) => setFiles(prev => [...prev, ...Array.from(list || [])].slice(0, 20));

  const extract = async () => {
    if (!files.length) { toast.error('Bitte Screenshots oder eine Excel/CSV-Datei auswählen'); return; }
    setExtracting(true);
    try {
      const r = await api.infraExtractDeals(fundId, files);
      setRows(r.data.rows || []); setErrors(r.data.errors || []);
      const inc = {}; (r.data.rows || []).forEach((row, i) => { inc[i] = !row.duplicate; });
      setInclude(inc);
      const dups = (r.data.rows || []).filter(x => x.duplicate).length;
      toast.success(`${(r.data.rows || []).length} Deals erkannt${dups ? ` · ${dups} bereits vorhanden` : ''}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Auslesen fehlgeschlagen'); }
    setExtracting(false);
  };

  const save = async () => {
    const chosen = (rows || []).filter((_, i) => include[i]);
    if (!chosen.length) { toast.error('Keine Deals ausgewählt'); return; }
    setSaving(true);
    try {
      const r = await api.infraSaveDeals(fundId, chosen);
      toast.success(`${r.data.saved} Deals gespeichert${r.data.skipped_duplicates ? ` · ${r.data.skipped_duplicates} Dubletten übersprungen` : ''}`);
      onSaved(); close();
    } catch (e) { toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    setSaving(false);
  };

  const saveManual = async () => {
    if (!(manual.headline || manual.target)) { toast.error('Überschrift oder Target eingeben'); return; }
    setSaving(true);
    try {
      const r = await api.infraSaveDeals(fundId, [{ ...manual, source: 'manual' }]);
      if (r.data.saved) { toast.success('Deal gespeichert'); onSaved(); setManual({}); }
      else toast.error('Deal existiert bereits');
    } catch (e) { toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    setSaving(false);
  };

  const selectedCount = Object.values(include).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="bg-card border-border rounded-sm max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="deal-import-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
            <Upload className="w-5 h-5 text-amber-400" /> Deals einspielen — {fundName}
          </DialogTitle>
        </DialogHeader>

        {!rows && (
          <div className="space-y-3">
            <div onClick={() => inputRef.current?.click()} onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
              data-testid="deal-import-dropzone"
              className="border border-dashed border-amber-500/40 rounded-sm p-6 text-center cursor-pointer hover:bg-amber-500/[0.04] transition-colors">
              <p className="text-sm text-foreground">Screenshots der Deal-Tabelle (PNG/JPG/WEBP) oder Excel/CSV hierher ziehen</p>
              <p className="text-[11px] text-muted-foreground mt-1">Mehrere Dateien auf einmal möglich — die KI liest jede Zeile aus und erkennt Dubletten.</p>
              <input ref={inputRef} type="file" multiple accept=".png,.jpg,.jpeg,.webp,.xlsx,.xls,.csv" className="hidden" data-testid="deal-import-input"
                onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
            </div>
            {files.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="deal-import-files">
                {files.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-1 text-[11px] border border-border rounded-sm px-2 h-7 text-foreground">
                    {f.type.startsWith('image/') ? <ImageIcon className="w-3 h-3 text-amber-300" /> : <FileSpreadsheet className="w-3 h-3 text-emerald-300" />}
                    {f.name}
                    <button onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-red-400"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex justify-end">
              <Button onClick={extract} disabled={extracting || !files.length} data-testid="deal-import-extract-btn"
                className="bg-amber-600 hover:bg-amber-500 text-white rounded-sm text-xs h-8 px-3">
                {extracting ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> KI liest aus…</> : `${files.length || ''} Datei(en) auslesen`}
              </Button>
            </div>

            <div className="border-t border-border/50 pt-3">
              <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2 inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Oder manuell hinzufügen</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
                {MANUAL_FIELDS.map(([k, label, ph]) => (
                  <input key={k} value={manual[k] || ''} onChange={e => setManual(prev => ({ ...prev, [k]: e.target.value }))} placeholder={`${label} — ${ph}`}
                    data-testid={`deal-manual-${k}`}
                    className="bg-secondary border border-border rounded-sm px-2 h-8 text-xs text-foreground focus:outline-none focus:border-amber-500/60" />
                ))}
              </div>
              <div className="flex justify-end mt-2">
                <Button variant="ghost" onClick={saveManual} disabled={saving} data-testid="deal-manual-save-btn" className="text-xs h-8 px-3 border border-border">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Deal speichern'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {rows && (
          <div className="space-y-3">
            {errors.length > 0 && <div className="text-[11px] text-red-400" data-testid="deal-import-errors">{errors.map((e, i) => <p key={i}>{e}</p>)}</div>}
            <div className="border border-border rounded-sm overflow-x-auto">
              <table className="w-full text-[11px]" data-testid="deal-import-preview">
                <thead className="bg-secondary/40 text-muted-foreground text-left">
                  <tr>
                    <th className="px-2 py-1.5 w-8"><Checkbox checked={selectedCount === rows.length && rows.length > 0} onCheckedChange={v => { const inc = {}; rows.forEach((_, i) => { inc[i] = !!v; }); setInclude(inc); }} /></th>
                    <th className="px-2 py-1.5">Deal / Target</th><th className="px-2 py-1.5">Fund</th><th className="px-2 py-1.5">Wert</th>
                    <th className="px-2 py-1.5">Status</th><th className="px-2 py-1.5">Datum</th><th className="px-2 py-1.5">Sektor</th><th className="px-2 py-1.5">Land / Region</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className={`border-t border-border/40 ${r.duplicate ? 'opacity-50' : ''}`} data-testid={`deal-import-row-${i}`}>
                      <td className="px-2 py-1.5"><Checkbox checked={!!include[i]} onCheckedChange={v => setInclude(prev => ({ ...prev, [i]: !!v }))} /></td>
                      <td className="px-2 py-1.5">
                        <p className="text-foreground">{r.headline}</p>
                        <p className="text-amber-300/80">{r.target}{r.duplicate && <span className="ml-2 text-[9px] font-bold text-muted-foreground border border-border rounded px-1">BEREITS VORHANDEN</span>}</p>
                        {r.description && <p className="text-muted-foreground/70 italic line-clamp-2 max-w-[360px]">{r.description}</p>}
                      </td>
                      <td className="px-2 py-1.5 text-muted-foreground">{r.fund_vehicle}</td>
                      <td className="px-2 py-1.5 font-mono">{r.value}</td>
                      <td className="px-2 py-1.5">{r.status}</td>
                      <td className="px-2 py-1.5 font-mono">{r.date}</td>
                      <td className="px-2 py-1.5">{r.sector}</td>
                      <td className="px-2 py-1.5">{[r.country, r.region].filter(Boolean).join(' · ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between gap-2">
              <button onClick={() => { setRows(null); setInclude({}); }} className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2">Andere Dateien wählen</button>
              <Button onClick={save} disabled={saving || !selectedCount} data-testid="deal-import-save-btn"
                className="bg-amber-600 hover:bg-amber-500 text-white rounded-sm text-xs h-8 px-3">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : `${selectedCount} Deals speichern`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
